/**
 * @author: Marcos Cordo
 * @version: 16/05/2026_PB (Punto B: type y department se gestionan en estimation.project, no en estimation directamente)
 */
import { Component, Input, Output, EventEmitter, SimpleChanges, OnInit } from '@angular/core';
import { Estimation } from '../../models/estimacion.model';
import { Project } from '../../models/project.model';
import { SocietyService } from '../../services/society.service';
import { DepartmentService } from '../../services/department.service';
import { TypeService } from '../../services/type.service';
import { SystemService } from '../../services/systems.service';
import { Department } from '../../models/department.model';
import { Society } from '../../models/society.model';
import { Type } from '../../models/type.model';
import { System } from '../../models/system.model';
import { LoggerService } from '../../services/logger.service';

@Component({
  selector: 'app-estimacion-details',
  templateUrl: './estimacion-details.component.html',
  styleUrls: ['./estimacion-details.component.css']
})
export class EstimacionDetailsComponent implements OnInit {
  @Input() estimation: Estimation = new Estimation();
  @Output() estimationChange = new EventEmitter<Estimation>();
  selectedSociety: Society = new Society();
  societies: Society[] = [];
  departments: Department[] = [];
  allDepartments: Department[] = [];
  types: Type[] = [];

  // NUEVO 1a
  allSystems: System[] = [];
  filteredSystems: System[] = [];

  constructor(
    private societyService: SocietyService,
    private departmentService: DepartmentService,
    private typeService: TypeService,
    private systemService: SystemService, // NUEVO 1a
    private logger: LoggerService
  ) {}

  ngOnInit(): void {
    this.logger.info("estimation-details.ngOnInit.");
    this.loadDepartments();
    this.loadSocieties();
    this.loadTypes();
    this.loadSystems(); // NUEVO 1a
  }

  ngOnChanges(changes: SimpleChanges) {
    this.logger.info("estimation-details.ngOnChanges.");
    if (!this.estimation.project || !this.estimation.project.id) {
      // Punto B: garantizar que project existe con type/department inicializados
      // antes de que los ngModel del template intenten acceder a ellos.
      if (!this.estimation.project) {
        this.estimation.project = new Project();
      }
    }
    if(this.estimation.id===undefined) {
      this.logger.info("estimation-details.ngOnChanges. estimation undefined.");
      this.setFechaActual();
      this.selectedSociety = new Society();
      if (!Array.isArray(this.estimation.systems)) {
        this.estimation.systems = [];
      }
      this.onSocietyChange();
    }
  }

  loadSocieties(): void {
    this.societyService.getAllSocieties().subscribe(
      (data) => {
        this.societies = data;
        this.selectSociety();
      },
      (error) => { this.logger.error('Error loading societies', error); }
    );
  }

  selectSociety(): void {
    const foundSociety = this.societies.find(d => d.id === this.estimation.project.department?.society?.id);
    if (foundSociety) {
      this.selectedSociety = foundSociety;
      this.estimation.project.department.society = foundSociety;
    } else {
      this.selectedSociety = new Society();
      this.estimation.project.department.society = new Society();
    }
    this.refreshFilteredSystems();
  }

  /**
   * MODIFICADO 1a: aplica la **Interpretación A** del manejo de sistemas:
   * misma cadena society→client → preservar sistemas; cambio de cliente → limpiar.
   */
  onSocietyChange(): void {
    const previousClientId = this.estimation?.project?.department?.society?.client?.id;
    const newClientId = this.selectedSociety?.client?.id;

    this.filterDepartmentsBySociety();
    this.estimation.project.department = new Department();

    if (!newClientId || newClientId !== previousClientId) {
      this.estimation.systems = [];
    }
    this.refreshFilteredSystems();

    this.onEstimationChange();
  }

  compareSocieties(s1: Society, s2: Society): boolean {
    return s1 && s2 ? s1.id === s2.id : s1 === s2;
  }

  loadDepartments(): void {
    this.departmentService.getAllDepartments().subscribe(
      (data) => {
        this.allDepartments = data;
        this.filterDepartmentsBySociety();
      },
      (error) => { this.logger.error('Error loading departments', error); }
    );
  }

  filterDepartmentsBySociety(): void {
    if (this.selectedSociety && this.selectedSociety.id) {
      this.departments = this.allDepartments.filter(dep => dep.society.id === this.selectedSociety.id);
    } else {
      this.departments = [...this.allDepartments];
    }
  }

  onDepartmentChange(): void {
    const selectedDepartment = this.estimation.project.department;
    if (selectedDepartment && selectedDepartment.society) {
      this.estimation.project.department.society = selectedDepartment.society;
      this.selectedSociety = selectedDepartment.society;
    }
    this.onEstimationChange();
  }

  compareDepartments(d1: Department, d2: Department): boolean {
    return d1 && d2 ? d1.id === d2.id : d1 === d2;
  }

  loadTypes(): void {
    this.typeService.getAllTypes().subscribe(
      (data) => {
        this.types = data;
        this.selectType();
      },
      (error) => { this.logger.error('Error loading types', error); }
    );
  }

  selectType(): void {
    const foundType = this.types.find(d => d.id === this.estimation.project.type?.id);
    if (foundType) {
      this.estimation.project.type = foundType;
    } else {
      this.estimation.project.type = new Type();
    }
  }

  compareTypes(d1: Type, d2: Type): boolean {
    return d1 && d2 ? d1.id === d2.id : d1 === d2;
  }

  // NUEVO 1a
  loadSystems(): void {
    this.systemService.getAllSystems().subscribe(
      (data) => { this.allSystems = data || []; this.refreshFilteredSystems(); },
      (error) => { this.logger.error('Error loading systems', error); }
    );
  }

  // NUEVO 1a
  refreshFilteredSystems(): void {
    const clientId = this.selectedSociety?.client?.id;
    if (!clientId) {
      this.filteredSystems = [];
      return;
    }
    this.filteredSystems = this.allSystems.filter(s => s?.client?.id === clientId);
  }

  // NUEVO 1a
  onSystemsChange(systems: System[]): void {
    this.estimation.systems = systems || [];
    this.onEstimationChange();
  }

  onEstimationChange(): void {
    this.logger.info("estimation-details.onEstimationChange.");
    this.estimationChange.emit(this.estimation);
  }

  setFechaActual() {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    this.estimation.createdate = `${year}-${month}-${day}`;
  }
}